import * as React from 'react';
import * as styled from './styled';
import {Viewer} from 'components/Viewer';
import {useRoute} from 'react-router5';
import {FoodItem, ProgramObject, TrainingItem} from 'api/program/types';
import useLoading from 'hooks/use-loading';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {getProgram} from '../../api/program/requests';
import {Exercise} from './components/Exercise';
import {Recipe} from '../FoodViewer/components/Recipe';

/**
 * The training viewer.
 * @return {React.ReactElement}
 * @constructor
 */
export default function TrainingViewer(): React.ReactElement {
  const {route} = useRoute();
  const {fetch, data, render} = useLoading<ProgramObject<TrainingItem>>();

  React.useEffect(() => {
    fetchProgram(route.params.trainingId);
  }, [route.params.trainingId]);

  /**
   * Fetch the recipe.
   * @return {void}
   */
  function fetchProgram(trainingId: ProgramObject<TrainingItem>['id']): void {
    fetch(() => new Promise((resolve) => (
      getProgram<TrainingItem>(trainingId).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )))
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render header.
     * @return {JSX.Element | undefined}
     */
    function renderHeader(): JSX.Element | undefined {
      return data ? (
        <Viewer.Header
          preview={data.preview}
          title={data.title}
          description={data.description}
          authorId={data.authorId}
        />
      ) : undefined;
    }

    /**
     * Render content.
     * @return {JSX.Element}
     */
    function renderContent(): JSX.Element {
      /**
       * Render section.
       * @param {string} title
       * @param {FoodItem["itemType"]} itemType
       * @return {JSX.Element | undefined}
       */
      function renderSection(
        title: string,
        itemType: TrainingItem['itemType'],
      ): JSX.Element | undefined {
        const items = data?.days[0].items.filter((item) => (
          item.itemType === itemType
        ));

        return items !== undefined && items.length > 0 ? (
          <div>
            <div>{title}</div>
            <div>
              {items.map((item, index) => (
                <Exercise
                  key={index}
                  {...item}
                  index={index}
                />
              ))}
            </div>
          </div>
        ) : undefined;
      }

      return (
        <React.Fragment>
          {renderSection('Разминка', 'warm-up')}
          {renderSection('Основная чать', 'main')}
        </React.Fragment>
      );
    }

    return (
      <Viewer>
        {{
          header: renderHeader(),
          content: renderContent(),
        }}
      </Viewer>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <p>The training program not found!</p>
    );
  }

  return render(renderContent, renderNotFound);
}
